import { getField, updateField } from "vuex-map-fields";
import stakeholderService from "../../services/stakeholderService";
import emailService from "../../services/emailService";

const state = {
  form: {
    fname: null,
    lname: null,
    address: null,
    city: null,
    state: null,
    postalCode: null,
    phone: null,
    email: null,
    longitude: null,
    latitude: null,
  },
  stakeholders: [],
  contactID: null,
  currentStakeholder: {},
  currentStakeholderOriginal: {},
  currentStakeholderID: null,
  currentStakeholderLocation: null,
  showRegisterModal: false,
  showUpdateModal: false,
  showNetworkAlert: false,
  showDuplicateAlert: false,
  surveyEmailStatus: null,
  stakeholderEmailStatus: null,
  stakeholderNames: []
};

const actions = {
  async addStakeholder({ commit }, payload) {
    let response = await stakeholderService.postStakeholder(payload);
    commit("SET_CURRENT_STAKEHOLDER_ID", response.data.pk);
    commit("SET_CONTACT_ID", response.data.pk);
  },
  fetchStakeholders({ commit }) {
    stakeholderService.fetchStakeholders().then(stakeholders => {
      commit("FETCH_STAKEHOLDERS", stakeholders);
    });
  },
  stakeholderLookUp({ commit }, stakeholder) {
    stakeholderService.stakeholderLookUp(stakeholder).then(response => {
      if (response.data.length > 1) {
        commit("SHOW_DUPLICATE_ALERT");
      } else if (response.data.length === 1) {
        commit("SET_CURRENT_STAKEHOLDER", response.data[0]);
        commit("SET_CURRENT_STAKEHOLDER_ORIGINAL", response.data[0]);
        commit("SET_CURRENT_STAKEHOLDER_ID", response.data[0].pk);
        commit("SET_CONTACT_ID", response.data[0].pk);
        if (state.currentStakeholder) {
          // only toggle modal if currentStakeholder is not undefined
          commit("TOGGLE_UPDATE_MODAL");
        }
      } else {
        commit("TOGGLE_REGISTER_MODAL");
      }
    });
  },
  async updateStakeholder({ commit }, stakeholder) {
    let response = await stakeholderService.updateStakeholder(stakeholder);
    commit("SET_CURRENT_STAKEHOLDER", response.data);
    commit("SET_CURRENT_STAKEHOLDER_ORIGINAL", response.data);
  },
  async createGeocode({ commit }, payload) {
    let coordinates = await stakeholderService.createGeocode(payload);
    commit("SET_CURRENT_STAKEHOLDER_LOCATION", coordinates);
    return coordinates;
  },
  setContactID({ commit }, id) {
    commit("SET_CONTACT_ID", id);
  },
  setCurrentStakeholder({ commit }, stakeholder) {
    commit("SET_CURRENT_STAKEHOLDER", stakeholder);
  },
  setCurrentStakeholderOriginal({ commit }, stakeholder) {
    commit("SET_CURRENT_STAKEHOLDER_ORIGINAL", stakeholder);
  },
  setCurrentStakeholderID({ commit }, id) {
    commit("SET_CURRENT_STAKEHOLDER_ID", id);
  },
  clearCurrentStakeholder({ commit }) {
    commit("CLEAR_CURRENT_STAKEHOLDER");
  },
  showNetworkAlert({ commit }) {
    commit("SHOW_NETWORK_ALERT");
  },
  showDuplicateAlert({ commit }) {
    commit("SHOW_DUPLICATE_ALERT");
  },
  toggleRegisterModal({ commit }) {
    commit("TOGGLE_REGISTER_MODAL");
  },
  toggleUpdateModal({ state, commit }) {
    if (state.currentStakeholder) {
      // only toggle modal if currentStakeholder is not undefined
      commit("TOGGLE_UPDATE_MODAL");
    }
  },
  emailAllStakeholders({ commit }, payload) {
    return stakeholderService
      .emailAllStakeholders(payload)
      .then(response => {
        commit("SET_STAKEHOLDER_EMAIL_STATUS", response);
      })
      .catch(error => {
        commit("SET_STAKEHOLDER_EMAIL_STATUS", error);
      });
  },
  getStakeholders({ commit }) {
    stakeholderService.fetchStakeholders().then(response => {
      commit("SET_STAKEHOLDERS", response);
    });
  },
  emailAfterSurveyResponse({ commit }, payload) {
    return emailService
      .emailStakeholderOnSurveySubmittal(payload)
      .then(response => {
        commit("SET_SURVEY_EMAIL_STATUS", response);
      })
      .catch(error => {
        commit("SET_SURVEY_EMAIL_STATUS", error);
      });
  },
  inviteStakeholderToConversation({ commit }, payload) {
    return stakeholderService
      .inviteStakeholderToConversation(payload)
      .then(response => {
        commit("SET_SURVEY_EMAIL_STATUS", response);
      })
      .catch(error => {
        commit("SET_SURVEY_EMAIL_STATUS", error);
      });
  },
  notifyStakeholder({ commit }, payload) {
    return stakeholderService
      .notifyStakeholder(payload)
      .then(response => {
        commit("SET_SURVEY_EMAIL_STATUS", response);
      })
      .catch(error => {
        commit("SET_SURVEY_EMAIL_STATUS", error);
      });
  },
  getStakeholderNames({ commit }) {
    return stakeholderService.getStakeholderNames()
      .then(response => {
        commit("SET_STAKEHOLDER_NAMES", response.data)
      })
  }
};

const mutations = {
  updateField,
  FETCH_STAKEHOLDERS(state, stakeholders) {
    state.stakeholders = stakeholders;
  },
  SET_CONTACT_ID(state, contactID) {
    state.contactID = contactID;
  },
  CLEAR_CURRENT_STAKEHOLDER(state) {
    state.currentStakeholderID = null;
    state.contactID = null;

    // this was previously setting all form fields to empty strings, which cannot easily be tested as `nullish`
    Object.keys(state.form).forEach(field => {
      state.form[field] = null;
    });

    state.showRegisterModal = false;
    state.showUpdateModal = false;
    state.showNetworkAlert = false;
  },
  SET_CURRENT_STAKEHOLDER(state, currentStakeholder) {
    state.currentStakeholder = currentStakeholder;
  },
  SET_CURRENT_STAKEHOLDER_ORIGINAL(state, currentStakeholderOriginal) {
    state.currentStakeholderOriginal = currentStakeholderOriginal;
  },
  SET_CURRENT_STAKEHOLDER_ID(state, currentStakeholderID) {
    state.currentStakeholderID = currentStakeholderID;
  },
  TOGGLE_REGISTER_MODAL(state) {
    state.showRegisterModal = !state.showRegisterModal;
  },
  TOGGLE_UPDATE_MODAL(state) {
    state.showUpdateModal = !state.showUpdateModal;
  },
  SHOW_NETWORK_ALERT(state) {
    state.showNetworkAlert = true;
  },
  SHOW_DUPLICATE_ALERT(state) {
    state.showDuplicateAlert = true;
  },
  SET_STAKEHOLDERS(state, stakeholders) {
    state.stakeholders = stakeholders;
  },
  SET_CURRENT_STAKEHOLDER_LOCATION(state, coordinates) {
    state.currentStakeholderLocation = coordinates;
  },
  SET_STAKEHOLDER_EMAIL_STATUS(state, status) {
    state.stakeholderEmailStatus = status;
  },
  SET_SURVEY_EMAIL_STATUS(state, status) {
    state.surveyEmailStatus = status;
  },
  SET_STAKEHOLDER_NAMES(state, names) {
    state.stakeholderNames = names;
  }
};

const getters = {
  form: state => {
    return state.form;
  },
  getField
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
};
